// src/hooks/useAuth.js
// Central auth state hook — all components should use this instead of reading localStorage directly.
import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

/**
 * Decode a JWT payload without verifying the signature (client-side only).
 * Returns null if the token is malformed or expired.
 */
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Check expiry: JWT exp is in seconds
    if (payload.exp && Date.now() / 1000 > payload.exp) {
      return null; // expired
    }
    return payload;
  } catch {
    return null;
  }
};

/**
 * Get time until token expiry in seconds
 */
const getTimeUntilExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenWarningShown, setTokenWarningShown] = useState(false);

  // On mount: restore auth state from localStorage and validate token
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      const decoded = decodeToken(storedToken);
      if (decoded) {
        // Token is valid and not expired
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } else {
        // Token is expired or invalid — clear stale auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
      }
    }

    setLoading(false);
  }, []);

  // Monitor token expiry and refresh before it expires
  useEffect(() => {
    if (!token) return;

    const checkAndRefreshToken = async () => {
      const timeUntilExpiry = getTimeUntilExpiry(token);
      
      if (!timeUntilExpiry) return;

      // Warn 2 minutes before expiry
      if (timeUntilExpiry < 120 && !tokenWarningShown) {
        setTokenWarningShown(true);
        // Dispatch event for app to show toast
        window.dispatchEvent(new CustomEvent('tokenExpiring', {
          detail: { secondsUntilExpiry: timeUntilExpiry }
        }));
        logger.warn('Token expiring soon', { secondsUntilExpiry: timeUntilExpiry });
      }

      // Refresh token 1 minute before expiry
      if (timeUntilExpiry < 60) {
        await refreshToken();
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkAndRefreshToken, 30000);
    // Also check immediately
    checkAndRefreshToken();

    return () => clearInterval(interval);
  }, [token, tokenWarningShown]);

  /** Call this after a successful login API response */
  const login = useCallback((userData, authToken, refreshTokenValue) => {
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    if (refreshTokenValue) {
      localStorage.setItem('refreshToken', refreshTokenValue);
    }
    setToken(authToken);
    setUser(userData);
    setTokenWarningShown(false);
  }, []);

  /** Refresh access token using refresh token */
  const refreshToken = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) {
        logger.warn('No refresh token available');
        return false;
      }

      logger.info('Attempting to refresh access token');
      const response = await fetch(
        (process.env.REACT_APP_API_URL || 'http://localhost:5001/api') + '/auth/refresh',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: storedRefreshToken }),
        }
      );

      const data = await response.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        setToken(data.accessToken);
        setTokenWarningShown(false);
        logger.info('Access token refreshed successfully');
        return true;
      } else {
        // Refresh failed, logout user
        logout();
        window.dispatchEvent(new Event('sessionExpired'));
        return false;
      }
    } catch (error) {
      logger.error('Token refresh failed', { error: error.message });
      logout();
      window.dispatchEvent(new Event('sessionExpired'));
      return false;
    }
  }, []);

  /** Call this to log out — clears state and localStorage */
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    setToken(null);
    setUser(null);
    setTokenWarningShown(false);
  }, []);

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading,
    login,
    logout,
    refreshToken,
  };
};
